import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, DollarSign, Calendar, MessageCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLeaseStore } from '../stores/leaseStore';
import { formatPrice } from '../utils/formatters';

interface SubleaseRequestProps {
  listing: any;
  onClose: () => void;
}

const SubleaseRequest: React.FC<SubleaseRequestProps> = ({ listing, onClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addRequest } = useLeaseStore();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    monthlyIncome: '',
    creditScore: '',
    employmentStatus: '',
    employmentLength: '',
    moveInDate: '',
    moveOutDate: '',
    message: ''
  });

  const creditScoreRanges = [
    { value: '850-800', label: 'Excellent (800-850)' },
    { value: '799-740', label: 'Very Good (740-799)' },
    { value: '739-670', label: 'Good (670-739)' },
    { value: '669-580', label: 'Fair (580-669)' },
    { value: '579-300', label: 'Poor (300-579)' }
  ];

  const employmentStatuses = [
    'Full-time',
    'Part-time',
    'Self-employed',
    'Student',
    'Retired',
    'Unemployed'
  ];

  const employmentLengths = [
    'Less than 1 year',
    '1-2 years',
    '2-5 years',
    '5-10 years',
    '10+ years'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      navigate('/login');
      return;
    }

    setIsSubmitting(true);
    try {
      // Calculate default move-out date if not provided (6 months from move-in)
      const moveOutDate = formData.moveOutDate || (() => {
        const date = new Date(formData.moveInDate);
        date.setMonth(date.getMonth() + 6);
        return date.toISOString().split('T')[0];
      })();

      const requestId = addRequest({
        listingId: listing.id,
        applicant: {
          id: user.id,
          name: user.fullName || '',
          email: user.email,
          phone: user.phone || '',
          occupation: user.occupation || '',
          monthlyIncome: parseInt(formData.monthlyIncome)
        },
        listing: {
          title: listing.title,
          price: listing.price,
          location: listing.location,
          image: listing.images[0],
          ownerId: listing.ownerId
        },
        moveInDate: formData.moveInDate,
        moveOutDate,
        message: formData.message
      });

      onClose();
      navigate('/dashboard?tab=applications');
    } catch (error) {
      console.error('Failed to submit application:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-xl w-full max-w-2xl my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Apply for Sublease</h2>
            <p className="text-sm text-gray-600">{listing.title}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          {/* Financial Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monthly Income
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  <input
                    type="number"
                    value={formData.monthlyIncome}
                    onChange={(e) => setFormData({ ...formData, monthlyIncome: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter monthly income"
                    required
                  />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Minimum required: {formatPrice(listing.price * 3)}/month
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Credit Score Range
                </label>
                <select
                  value={formData.creditScore}
                  onChange={(e) => setFormData({ ...formData, creditScore: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  required
                >
                  <option value="">Select range</option>
                  {creditScoreRanges.map((range) => (
                    <option key={range.value} value={range.value}>
                      {range.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Employment Status
                </label>
                <select
                  value={formData.employmentStatus}
                  onChange={(e) => setFormData({ ...formData, employmentStatus: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  required
                >
                  <option value="">Select status</option>
                  {employmentStatuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Length of Employment
                </label>
                <select
                  value={formData.employmentLength}
                  onChange={(e) => setFormData({ ...formData, employmentLength: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  required
                >
                  <option value="">Select length</option>
                  {employmentLengths.map((length) => (
                    <option key={length} value={length}>
                      {length}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Lease Period */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Lease Period</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Move-in Date
                </label>
                <input
                  type="date"
                  value={formData.moveInDate}
                  onChange={(e) => setFormData({ ...formData, moveInDate: e.target.value })}
                  min={listing.available}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Move-out Date (Optional)
                </label>
                <input
                  type="date"
                  value={formData.moveOutDate}
                  onChange={(e) => setFormData({ ...formData, moveOutDate: e.target.value })}
                  min={formData.moveInDate}
                  max={listing.endDate}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Defaults to 6 months if not specified
                </p>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message to Owner
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Introduce yourself and explain why you'd be a great tenant..."
                required
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <MessageCircle className="h-5 w-5 mr-2" />
                  Submit Application
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubleaseRequest;